import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { api, jsonBody, queryString } from "../../api/client";
import type { Contact, ListResponse } from "../../api/types";
import { ErrorPanel } from "../../ui/components";
import { contactInput, useContact } from "./queries";

// One control for "who is this for", shared by the booking form, the event form, the finance
// filter and vendor billing.
//
// It replaces four copies of the same dropdown, each of which asked the server for the first
// hundred contacts and offered exactly those. That is not a limit anybody meets gently: the
// hundred come back ordered by name, so the customer who cannot be chosen is not the newest one,
// it is simply somebody whose name sorts late. A venue with two hundred contacts could not raise
// a booking for half of them, and the screen gave no sign that anything was missing. Four browser
// journeys failed the day the demo data crossed a hundred active contacts, which is the polite
// version of a customer finding out.
//
// So this is a search rather than a menu. The server already matches the person, their email and
// the organisation they act for in one query, and every pause in typing asks it again. What is on
// screen is a page of answers, never the whole set, and nobody is unreachable for sorting late.
//
// Creating belongs in the same control because the moment somebody discovers a customer is not on
// file is the moment they are booking them, usually with them on the phone; sending staff to
// another screen means abandoning a half-filled form. The one thing creation must never do is
// happen by accident, so it is always the last row and never the highlighted one. Enter takes the
// match under the cursor, and reaching creation is a deliberate arrow-down or a click.

// How many answers are shown at once. This is a display choice, not a ceiling on who can be
// found: somebody who cannot see their customer in ten types another letter rather than scrolling
// through a list that was never meant to be read end to end.
const SUGGESTIONS = 10;

// Long enough that a name is typed rather than spelled out one request per letter, short enough
// that the answer arrives while the hand is still on the keyboard.
const PAUSE_MS = 180;

export interface ContactPickerProps {
  // Omitted by the finance filter, which reads its value through onSelect rather than posting a
  // form. Everywhere else this is the field name the surrounding FormData expects.
  name?: string | undefined;
  id?: string | undefined;
  required?: boolean | undefined;
  defaultContactId?: string | undefined;
  placeholder?: string | undefined;
  // Marks this control as the one a modal should focus when it opens.
  autoFocus?: boolean | undefined;
  onSelect?: ((contact: Contact | null) => void) | undefined;
  "aria-describedby"?: string | undefined;
  "aria-invalid"?: boolean | undefined;
}

export function ContactPicker({
  name,
  id,
  required = false,
  defaultContactId,
  placeholder = "Search by name, organization or email",
  autoFocus = false,
  onSelect,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
}: ContactPickerProps) {
  const client = useQueryClient();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [selected, setSelected] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftOrganisation, setDraftOrganisation] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [working, setWorking] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);

  // An edit form knows the customer's id and nothing else, so the name to show has to be fetched.
  // It is adopted once: after that the field belongs to whoever is typing in it, and a late
  // response must not overwrite a choice already made.
  const existing = useContact(defaultContactId);
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current || !existing.data) return;
    adopted.current = true;
    setSelected(existing.data);
    setText(existing.data.displayName);
  }, [existing.data]);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(text.trim()), PAUSE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const matches = useQuery({
    queryKey: ["contacts", "picker", term],
    queryFn: () =>
      api<ListResponse<Contact>>(
        `/api/v1/contacts${queryString({ q: term, limit: SUGGESTIONS, offset: 0 })}`,
      ),
    enabled: open,
    // Without this the list empties itself between keystrokes and flashes at somebody mid-word.
    placeholderData: keepPreviousData,
  });

  // Archived contacts stay findable, because the person who booked the hall last year is the same
  // person booking it again, and making staff create a second record for them is how one customer
  // becomes two histories. They sort below everyone active rather than being hidden.
  const options = [...(matches.data?.items ?? [])].sort(
    (left, right) => Number(left.status === "archived") - Number(right.status === "archived"),
  );
  const typed = text.trim();
  const offersCreate = typed !== "";
  const rows = options.length + (offersCreate ? 1 : 0);

  // The highlight describes the list currently on screen, so it is recomputed as that list
  // changes rather than corrected afterwards. An effect would leave one rendered frame showing a
  // highlight belonging to the previous set of answers, and that frame is the one Enter can land
  // in. Landing on the first match is what lets Enter take the obvious one; creation is the last
  // row and is never highlighted this way, so it can only ever be reached on purpose.
  const [shown, setShown] = useState({ term: "", count: 0 });
  if (shown.term !== term || shown.count !== options.length) {
    setShown({ term, count: options.length });
    setActive(options.length > 0 ? 0 : -1);
  }

  useEffect(() => {
    // A hidden input cannot be required, so the visible control carries the requirement. This is
    // what stops a form being submitted with a customer typed but never matched to a record.
    inputRef.current?.setCustomValidity(
      required && !selected ? "Choose a contact from the list, or create a new one." : "",
    );
  }, [required, selected]);

  function pick(contact: Contact) {
    setSelected(contact);
    setText(contact.displayName);
    setOpen(false);
    setCreating(false);
    setFailure(undefined);
    onSelect?.(contact);
  }

  // Typing away from the chosen contact drops the choice. The alternative is a field reading one
  // name while the id underneath it points at somebody else, which submits without complaint.
  function retype(value: string) {
    setText(value);
    setOpen(true);
    if (selected && value !== selected.displayName) {
      setSelected(null);
      onSelect?.(null);
    }
  }

  function startCreating() {
    // Somebody who typed an address was searching for an address, so it is kept as one rather than
    // being offered back to them as a person's name.
    const looksLikeEmail = typed.includes("@");
    setDraftName(looksLikeEmail ? "" : typed);
    setDraftEmail(looksLikeEmail ? typed : "");
    setDraftOrganisation("");
    setFailure(undefined);
    setCreating(true);
    setOpen(false);
  }

  async function create() {
    setWorking(true);
    setFailure(undefined);
    try {
      const organisation = draftOrganisation.trim();
      const contact = await api<Contact>("/api/v1/contacts", {
        method: "POST",
        body: jsonBody({
          // Somebody acting for a company is that company's contact; somebody acting for nobody is
          // an individual, and the server clears the company fields on one of those. Reading it off
          // whether an organisation was given keeps a question off a form filled in mid-
          // conversation. Type, status and address all have server-side defaults; tags does not,
          // because the column is NOT NULL and an absent list is sent as null.
          type: organisation ? "organisation" : "individual",
          displayName: draftName.trim(),
          organisation,
          email: draftEmail.trim(),
          tags: [],
        }),
      });
      await client.invalidateQueries({ queryKey: ["contacts"] });
      // Chosen from the response rather than from a refreshed list: the contact is known here, and
      // a read issued now could still be answered by one that started before the write.
      pick(contact);
    } catch (caught) {
      setFailure(caught);
    } finally {
      setWorking(false);
    }
  }

  async function restore(contact: Contact) {
    setWorking(true);
    setFailure(undefined);
    try {
      // The whole record goes back with the version it was read at, so bringing somebody out of
      // the archive cannot blank the fields nobody touched or overwrite an edit made since.
      const restored = await api<Contact>(`/api/v1/contacts/${contact.id}`, {
        method: "PUT",
        body: jsonBody({ ...contactInput(contact), status: "active" }),
      });
      await client.invalidateQueries({ queryKey: ["contacts"] });
      pick(restored);
    } catch (caught) {
      setFailure(caught);
    } finally {
      setWorking(false);
    }
  }

  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => Math.min(Math.max(current + step, -1), rows - 1));
      return;
    }
    if (event.key === "Enter") {
      if (!open) return;
      // Enter belongs to the list while the list is showing. Letting it through would submit the
      // form around this control with whatever is currently typed.
      event.preventDefault();
      const match = active >= 0 ? options[active] : undefined;
      if (match) pick(match);
      else if (offersCreate) startCreating();
      else setOpen(false);
      return;
    }
    if (event.key === "Escape" && (open || creating)) {
      // A modal closes on Escape from a listener on the document, so dismissing this list has to
      // stop there. Otherwise one key both closes the suggestions and throws away the form.
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setCreating(false);
      return;
    }
    if (event.key === "Tab") setOpen(false);
  }

  const address = draftEmail.trim().toLowerCase();
  // Email is a duplicate warning, not a key. Two of the demo venue's contacts already share an
  // office address, forty of them have no email at all, and the column carries no unique index, so
  // treating it as an identity would refuse legitimate records. Showing who else uses it lets the
  // person on the phone decide, which is the only party who actually knows.
  const sharing = useQuery({
    queryKey: ["contacts", "picker", "duplicate", address],
    queryFn: () =>
      api<ListResponse<Contact>>(
        `/api/v1/contacts${queryString({ q: address, limit: 5, offset: 0 })}`,
      ),
    enabled: creating && address.includes("@"),
  });
  const alreadyUsing = sharing.data?.items.find(
    (candidate) => candidate.email.toLowerCase() === address,
  );

  function createOnEnter(event: KeyboardEvent<HTMLInputElement>) {
    // The creation fields sit inside somebody else's form, because a form cannot be nested in a
    // form. Enter in one of them means "create this contact", never "submit the booking", so the
    // key is answered here and cancelled rather than left to reach the form underneath.
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (draftName.trim() && !working) void create();
  }

  return (
    <div className="picker">
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        data-autofocus={autoFocus || undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        onChange={(event) => retype(event.target.value)}
        onClick={() => setOpen(true)}
        onKeyDown={keyDown}
        // Choosing from the list never moves focus, because each row refuses the mousedown that
        // would take it. So focus leaving this input always means the list is finished with.
        onBlur={() => setOpen(false)}
      />
      {name ? <input type="hidden" name={name} value={selected?.id ?? ""} /> : null}
      <span className="sr-only" role="status">
        {open ? `${options.length} of your contacts match` : ""}
      </span>
      {open ? (
        // A listbox of divs rather than a list of list items, because these rows are never focused
        // and never navigated as a list: focus stays on the combobox above and the highlight
        // travels by aria-activedescendant, which is what keeps typing and choosing in one place.
        <div className="picker-list" id={listId} role="listbox" aria-label="Matching contacts">
          {options.map((contact, index) => (
            // biome-ignore lint/a11y/useKeyWithClickEvents: Every key belongs to the combobox input above, which owns this listbox through aria-activedescendant; a row cannot be focused and so can never receive one.
            <div
              key={contact.id}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              className={`picker-option ${index === active ? "active" : ""}`}
              // Taking the mousedown keeps focus on the input, so the list does not close out from
              // under the click that was about to choose somebody.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(contact)}
            >
              <strong>
                {contact.displayName}
                {contact.status === "archived" ? (
                  <span className="picker-tag">Archived</span>
                ) : null}
              </strong>
              <small>
                {[contact.organisation, contact.email].filter(Boolean).join(" · ") ||
                  "No organization or email on file"}
              </small>
            </div>
          ))}
          {offersCreate ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: Every key belongs to the combobox input above, which owns this listbox through aria-activedescendant; a row cannot be focused and so can never receive one.
            <div
              id={`${listId}-${options.length}`}
              role="option"
              tabIndex={-1}
              aria-selected={active === options.length}
              className={`picker-option picker-new ${active === options.length ? "active" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={startCreating}
            >
              <strong>Create new contact</strong>
              <small>{typed}</small>
            </div>
          ) : null}
          {rows === 0 ? (
            <p className="picker-empty">No contacts yet. Type a name to add one.</p>
          ) : null}
        </div>
      ) : null}
      {selected && !creating ? (
        <p className="picker-chosen">
          <span>
            {[selected.organisation, selected.email].filter(Boolean).join(" · ") ||
              "No organization or email on file"}
          </span>
          {selected.status === "archived" ? (
            <>
              <span className="picker-tag">Archived</span>
              <button
                type="button"
                className="button secondary small"
                disabled={working}
                onClick={() => restore(selected)}
              >
                Restore
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {creating ? (
        <div className="picker-create">
          <strong>New contact</strong>
          <label>
            Full name
            <input
              // biome-ignore lint/a11y/noAutofocus: This panel opens only because somebody deliberately chose to create a contact, and this is the first field of the panel that choice opened.
              autoFocus
              required
              maxLength={200}
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={createOnEnter}
            />
          </label>
          <label>
            Organization
            <input
              maxLength={200}
              placeholder="Leave empty for a private individual"
              value={draftOrganisation}
              onChange={(event) => setDraftOrganisation(event.target.value)}
              onKeyDown={createOnEnter}
            />
          </label>
          <label>
            Email address
            <input
              type="email"
              value={draftEmail}
              onChange={(event) => setDraftEmail(event.target.value)}
              onKeyDown={createOnEnter}
            />
          </label>
          {alreadyUsing ? (
            <div className="picker-duplicate" role="status">
              <span>
                {alreadyUsing.email} is already used by <strong>{alreadyUsing.displayName}</strong>.
                Use them, or create a separate contact?
              </span>
              <button
                type="button"
                className="button secondary small"
                onClick={() => pick(alreadyUsing)}
              >
                Use {alreadyUsing.displayName}
              </button>
            </div>
          ) : null}
          {failure ? <ErrorPanel error={failure} /> : null}
          <div className="picker-create-actions">
            <button
              type="button"
              className="button secondary small"
              disabled={working}
              onClick={() => setCreating(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button primary small"
              disabled={working || draftName.trim() === ""}
              onClick={() => void create()}
            >
              {working ? "Working…" : "Create contact"}
            </button>
          </div>
          <small>
            A name is all that is needed now. An invoice issued against a contact with no billing
            address will say so, and the rest can be filled in from the contact record later.
          </small>
        </div>
      ) : null}
      {failure && !creating ? <ErrorPanel error={failure} /> : null}
    </div>
  );
}
